"use client";
import { useState, useEffect } from "react";
import { FiSettings } from "react-icons/fi"; // Settings icon

import Footer_Layout1 from "./FooterLayouts/Layout1"; // Simple_Strip
import Footer_Layout2 from "./FooterLayouts/Layout2"; // Info_Grid
import Footer_Layout3 from "./FooterLayouts/Layout3"; // Full_Feature
import Footer_Layout5 from "./FooterLayouts/Layout5";
import { Footer_Layout4 } from "./FooterLayouts/Layout4";
// import Footer_Layout5 from "./FooterLayouts/Layout5"; // Elegant_Divider

// Types
interface FooterLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  social?: string;
}

interface CategoryLink {
  id: string;
  name: string;
  slug: string;
}

interface Address {
  id: number;
  text: string;
  address: string;
  country: string;
}

type LayoutType =
  | "Simple_Strip"
  | "Info_Grid"
  | "Full_Feature"
  | "Social_Hub"
  | "Elegant_Divider";

interface FooterProps {
  logoUrl: string | null;
  logoText: string | null;
  menuLinks: FooterLink[];
  productLinks: CategoryLink[];
  categoryLinks: CategoryLink[];
  legalLinks: FooterLink[];
  eCommerceLinks: FooterLink[];
  socialLinks: FooterLink[];
  msClarityID: string;
  googleAnalyticsID: string;
  facebookPexelTrackerID: string;
  googleTagManagerID: string;
  addresses: Address[];
  images?: any;
  layoutFooter: LayoutType | null;
  navigationSection: any;
  navigation: any;
  contactInfo: any;
}

// Layout mapping
const renderLayout = (layout: LayoutType, props: FooterProps): JSX.Element => {
  switch (layout) {
    case "Simple_Strip":
      return <Footer_Layout1 {...(props as any)} />;
    case "Info_Grid":
      return <Footer_Layout2 {...props} />;
    case "Full_Feature":
      return <Footer_Layout3 {...props} />;
    case "Social_Hub":
      return <Footer_Layout4 {...props} />;
    case "Elegant_Divider":
      return <Footer_Layout5 {...props} />;
    default:
      return <Footer_Layout3 {...(props as any)} />;
  }
};

export default function Footer(props: FooterProps) {
  const { layoutFooter } = props;

  const [layout, setLayout] = useState<LayoutType>(
    layoutFooter ?? "Simple_Strip",
  );
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (layoutFooter) {
      setLayout(layoutFooter);
    }
  }, [layoutFooter]);

  const layoutOptions: {
    value: LayoutType;
    label: string;
    description: string;
  }[] = [
    {
      value: "Simple_Strip",
      label: "Simple Strip",
      description: "Minimal footer layout",
    },
    {
      value: "Info_Grid",
      label: "Info Grid",
      description: "Grid style information layout",
    },
    {
      value: "Full_Feature",
      label: "Full Feature",
      description: "All links and details included",
    },
    {
      value: "Social_Hub",
      label: "Social Hub",
      description: "Social links and interactions",
    },
    {
      value: "Elegant_Divider",
      label: "Elegant Divider",
      description: "Stylish footer layout",
    },
  ];

  return (
    <div className="relative">
      {renderLayout(layout, props)}

      {process.env.NODE_ENV === "development" && (
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="p-2 bg-white shadow-md border border-gray-200 rounded-full hover:bg-gray-100"
            title="Select Footer Layout"
          >
            <FiSettings size={20} />
          </button>

          {showDropdown && (
            <div className="mt-2 bg-white border border-gray-300 rounded-md shadow-lg p-4 w-48">
              <h4 className="font-semibold mb-2">Select Footer Layout</h4>
              <ul>
                {layoutOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      className={`block w-full text-left px-3 py-1 rounded hover:bg-gray-100 ${
                        layout === option.value ? "bg-gray-200 font-bold" : ""
                      }`}
                      onClick={() => {
                        setLayout(option.value);
                        setShowDropdown(false);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
